import { Injectable, signal, computed, inject } from '@angular/core';
import { TimelineTrackerService } from './timeline-tracker.service';
import { LogEntry } from '../../interfaces/dev-tool/console.interface';

// Re-export for backward compatibility
export { LogEntry };

@Injectable({ providedIn: 'root' })
export class DebugLoggerService {
  private logs = signal<LogEntry[]>([]);
  private maxLogs = 1000;

  // Store variables that can be accessed in console
  private variables = signal<Record<string, any>>({});

  getLogs = computed(() => this.logs());
  getVariables = computed(() => this.variables());

  private timelineService = inject(TimelineTrackerService);

  constructor() {
    // Intercept browser console methods
    this.interceptConsole();
  }

  log(message: string, data?: any) {
    this.addLog('log', message, data);
    this.timelineService.trackConsole('log', message, data);
  }

  warn(message: string, data?: any) {
    this.addLog('warn', message, data);
    this.timelineService.trackConsole('warn', message, data);
  }

  error(message: string, data?: any, error?: Error) {
    this.addLog('error', message, data, error?.stack);
    this.timelineService.trackConsole('error', message, data);
    if (error) {
      this.timelineService.trackError(error);
    }
  }

  info(message: string, data?: any) {
    this.addLog('info', message, data);
    this.timelineService.trackConsole('info', message, data);
  }

  command(message: string) {
    this.addLog('command', message);
  }

  result(message: string, data?: any) {
    this.addLog('result', message, data);
  }

  private addLog(type: LogEntry['type'], message: string, data?: any, stackTrace?: string) {
    const entry: LogEntry = {
      id: crypto.randomUUID(),
      timestamp: new Date(),
      type,
      message,
      data,
      stackTrace
    };

    this.logs.update(logs => {
      const newLogs = [...logs, entry];
      return newLogs.slice(-this.maxLogs);
    });
  }

  clear() {
    this.logs.set([]);
  }

  // Register variables that can be accessed in console
  setVariable(name: string, value: any) {
    this.variables.update(vars => ({
      ...vars,
      [name]: value
    }));
  }

  // Execute JavaScript code in console
  executeCode(code: string): { success: boolean; result?: any; error?: string } {
    try {
      // Show the command first
      this.command(code);

      // Create a context with available variables
      const context = this.variables();

      // Special handling for simple variable access
      const trimmedCode = code.trim();
      if (context.hasOwnProperty(trimmedCode)) {
        const result = context[trimmedCode];
        this.result(this.formatResult(result), result);
        return { success: true, result };
      }

      // Try to evaluate the expression
      try {
        // Create function with variables in scope
        const funcBody = `
          'use strict';
          return (${code});
        `;

        const func = new Function(...Object.keys(context), funcBody);
        const result = func(...Object.values(context));

        // Only show result if it's not undefined
        if (result !== undefined) {
          this.result(this.formatResult(result), result);
        }
        return { success: true, result };
      } catch (e: any) {
        // If that fails, try without parentheses (for statements)
        const func = new Function(...Object.keys(context), `
          'use strict';
          return ${code};
        `);

        const result = func(...Object.values(context));
        // Only show result if it's not undefined
        if (result !== undefined) {
          this.result(this.formatResult(result), result);
        }
        return { success: true, result };
      }
    } catch (error: any) {
      this.error('Error: ' + error.message);
      return { success: false, error: error.message };
    }
  }

  private formatResult(value: any): string {
    if (value === undefined) return 'undefined';
    if (value === null) return 'null';
    if (typeof value === 'string') return `"${value}"`;
    if (typeof value === 'object') return JSON.stringify(value, this.getCircularReplacer());
    return String(value);
  }

  private getCircularReplacer() {
    const seen = new WeakSet();
    return (_key: string, value: any) => {
      if (typeof value === "object" && value !== null) {
        if (seen.has(value)) {
          return '[Circular]';
        }
        seen.add(value);
      }
      return value;
    };
  }

  // Intercept browser console methods
  interceptConsole() {
    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;
    const originalInfo = console.info;

    console.log = (...args: any[]) => {
      // Still call original for debugging in browser console
      originalLog.apply(console, args);
      const { message, data } = this.buildConsolePayload(args);
      this.log(message, data);
    };

    console.warn = (...args: any[]) => {
      originalWarn.apply(console, args);
      const { message, data } = this.buildConsolePayload(args);
      this.warn(message, data);
    };

    console.error = (...args: any[]) => {
      originalError.apply(console, args);
      const { message, data } = this.buildConsolePayload(args);
      this.error(message, data);
    };

    console.info = (...args: any[]) => {
      originalInfo.apply(console, args);
      const { message, data } = this.buildConsolePayload(args);
      this.info(message, data);
    };
  }

  // NEW: generic payload builder – works for any combination of arguments
  private buildConsolePayload(args: any[]): { message: string; data?: any } {
    if (!args || args.length === 0) {
      return { message: '' };
    }

    // Separate primitives vs objects
    const objectArgs = args.filter(a => a !== null && typeof a === 'object');
    const primitiveArgs = args.filter(a => a === null || typeof a !== 'object');

    // Build message from primitives only
    const message = primitiveArgs
      .map(a => {
        if (a === null) return 'null';
        if (a === undefined) return 'undefined';
        return String(a);
      })
      .join(' ');

    // No objects at all → message only
    if (objectArgs.length === 0) {
      return {
        message: message || this.getObjectLabel(args[0])
      };
    }

    // Only objects, no primitives → label them
    if (!message) {
      const labels = objectArgs.map(o => this.getObjectLabel(o));
      return {
        message: labels.join(' '),
        data: objectArgs.length === 1 ? objectArgs[0] : objectArgs
      };
    }

    // Mixed primitives + objects
    return {
      message,
      data: objectArgs.length === 1 ? objectArgs[0] : objectArgs
    };
  }

  // NEW: small label for objects (used in message when only objects are logged)
  private getObjectLabel(obj: any): string {
    if (Array.isArray(obj)) {
      return `Array(${obj.length})`;
    }
    const ctorName = obj?.constructor?.name;
    return ctorName && ctorName !== 'Object' ? ctorName : '{…}';
  }

  trackAPICall(method: string, url: string, status: number, duration: number, data?: any) {
    const queryName = this.extractQueryName(url);
    const statusIcon = status >= 200 && status < 300 ? '✅' : '❌';
    const isSuccess = status >= 200 && status < 300;
    const logType = isSuccess ? 'api' : 'error';

    this.addLog(
      logType,
      `${statusIcon} ${queryName} ran successfully (${(duration / 1000).toFixed(3)}s)`,
      {
        method,
        url,
        status,
        duration,
        ...data
      }
    );
  }

  private extractQueryName(url: string): string {
    // Extract meaningful name from URL
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname;
      const segments = path.split('/').filter(s => s);

      // Return last segment or 'api'
      return segments.length > 0 ? segments[segments.length - 1] : 'query1';
    } catch {
      return 'query1';
    }
  }
}